import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, ArrowLeft, User, Users, FileText, Shield, Phone, Star } from "lucide-react";
import axios from "axios";
import SEOHead from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { id: 1, title: "Öğrenci Bilgileri", icon: User, desc: "Çocuğunuzun temel bilgileri" },
  { id: 2, title: "Veli Bilgileri", icon: Users, desc: "Velinin iletişim bilgileri" },
  { id: 3, title: "Ek Bilgiler", icon: FileText, desc: "Sağlık ve diğer bilgiler" },
];

export default function PreRegistrationPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const isTransitioning = useRef(false);
  const [form, setForm] = useState({
    student_name: "",
    student_surname: "",
    birth_date: "",
    gender: "",
    branch: "",
    parent_name: "",
    parent_surname: "",
    parent_phone: "",
    parent_email: "",
    address: "",
    health_notes: "",
    how_did_you_hear: "",
    notes: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setForm({ ...form, [name]: value });
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!form.student_name || !form.student_surname || !form.birth_date || !form.gender || !form.branch) {
        toast.error("Lütfen tüm zorunlu alanları doldurun.");
        return false;
      }
    }
    if (step === 2) {
      if (!form.parent_name || !form.parent_surname || !form.parent_phone || !form.parent_email) {
        toast.error("Lütfen veli bilgilerini eksiksiz doldurun.");
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      isTransitioning.current = true;
      setCurrentStep((prev) => Math.min(prev + 1, 3));
      // Reset transitioning flag after React has re-rendered
      setTimeout(() => { isTransitioning.current = false; }, 100);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent submission during step transition
    if (isTransitioning.current) return;
    // Only submit if user clicked the submit button (not during step transition)
    const submitter = e.nativeEvent?.submitter;
    const isSubmitButton = submitter?.getAttribute('data-testid') === 'pre-registration-submit';
    if (!isSubmitButton) return;
    if (currentStep !== 3) return;
    if (!validateStep(1) || !validateStep(2)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/pre-registration`, form);
      toast.success("Ön kaydınız başarıyla alındı!");
      setSubmitted(true);
    } catch {
      toast.error("Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="registration-success" className="pt-20 min-h-screen bg-surface flex items-center">
        <div className="max-w-lg mx-auto px-4 text-center py-32">
          <div className="w-24 h-24 mx-auto flex items-center justify-center bg-green-50 border-2 border-green-200 rounded-full mb-8">
            <CheckCircle className="w-12 h-12 text-green-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A]">
            Kaydınız Alındı!
          </h1>
          <p className="mt-4 text-gray-600 leading-relaxed">
            Ön kayıt formunuz başarıyla gönderildi. Ekibimiz en kısa sürede sizinle iletişime geçecektir.
          </p>
          <div className="mt-6 p-4 bg-burgundy/5 border border-burgundy/10">
            <div className="flex items-center gap-2 justify-center">
              <Phone className="w-4 h-4 text-burgundy" />
              <span className="text-sm text-[#1A1A1A] font-medium">Sorularınız için: 0543 647 01 83</span>
            </div>
          </div>
          <a href="/">
            <Button
              data-testid="success-home-btn"
              className="mt-8 bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-5"
            >
              Ana Sayfaya Dön
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pre-registration-page" className="pt-20">
      <SEOHead
        title={"Ön Kayıt Formu - Voleybol, Basketbol, Cimnastik Kayıt"}
        description={"İRÜ Spor Kulübü ön kayıt formu. Ataşehir, Kadıköy, Maltepe bölgesinde voleybol, basketbol veya cimnastik kursuna kayıt yaptırın. 5-14 yaş grubu için spor eğitimi."}
        keywords={"ön kayıt spor okulu, voleybol kursu kayıt Kadıköy, basketbol kursu kayıt Ataşehir, cimnastik kursu kayıt Maltepe, spor okulu başvuru, çocuk spor kayıt İstanbul"}
        canonical="/on-kayit"
      />
      {/* Hero - Güven Veren */}
      <section className="bg-[#1A1A1A] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-gold">Ön Kayıt</span>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white mt-2">
                Ön Kayıt Formu
              </h1>
              <p className="mt-3 text-base text-white/60 max-w-lg">
                Aşağıdaki formu doldurarak ön kayıt başvurunuzu yapabilirsiniz.
              </p>
              <p className="mt-2 inline-flex items-center gap-2 text-sm text-gold font-medium bg-gold/10 border border-gold/20 px-3 py-1.5">
                İlk antrenman ücretsizdir!
              </p>
            </div>
            {/* Güven Rozeti */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2.5">
                <Shield className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <span className="text-xs text-white/80 font-accent uppercase tracking-wider">Güvenli Kayıt</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2.5">
                <Star className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <span className="text-xs text-white/80 font-accent uppercase tracking-wider">Üniversite Güvencesi</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Area */}
      <section className="py-16 lg:py-24 bg-surface">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stepper */}
          <div className="mb-12" data-testid="form-stepper">
            <div className="flex items-center justify-between">
              {steps.map((step, i) => (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 flex items-center justify-center border-2 transition-all duration-300 ${
                        currentStep >= step.id
                          ? "bg-burgundy border-burgundy text-white"
                          : "bg-white border-zinc-300 text-zinc-400"
                      }`}
                    >
                      {currentStep > step.id ? (
                        <CheckCircle className="w-5 h-5" strokeWidth={2} />
                      ) : (
                        <step.icon className="w-5 h-5" strokeWidth={1.5} />
                      )}
                    </div>
                    <p className={`mt-2 text-xs font-heading uppercase tracking-wider text-center hidden sm:block ${
                      currentStep >= step.id ? "text-burgundy" : "text-zinc-400"
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-[10px] text-zinc-400 text-center mt-0.5 hidden lg:block">{step.desc}</p>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-2 mb-8 sm:mb-14 lg:mb-16 transition-colors duration-300 ${
                      currentStep > step.id ? "bg-burgundy" : "bg-zinc-300"
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault(); }} data-testid="pre-registration-form">
            <div className="bg-white border border-zinc-200 shadow-lg">
              <div className="p-6 sm:p-8 lg:p-10">
                {/* Step 1 - Öğrenci */}
                {currentStep === 1 && (
                  <div data-testid="step-1" className="space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-zinc-200 mb-6">
                      <div className="w-10 h-10 bg-burgundy/5 border border-burgundy/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-burgundy" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-[#1A1A1A]">
                          Öğrenci Bilgileri
                        </h2>
                        <p className="text-xs text-zinc-500">Çocuğunuzun temel bilgilerini girin</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="student_name" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                          Öğrenci Adı *
                        </Label>
                        <Input
                          id="student_name"
                          name="student_name"
                          value={form.student_name}
                          onChange={handleChange}
                          placeholder="Adını girin"
                          data-testid="student-name"
                          className="mt-1.5 rounded-none border-zinc-300 h-11"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="student_surname" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                          Öğrenci Soyadı *
                        </Label>
                        <Input
                          id="student_surname"
                          name="student_surname"
                          value={form.student_surname}
                          onChange={handleChange}
                          placeholder="Soyadını girin"
                          data-testid="student-surname"
                          className="mt-1.5 rounded-none border-zinc-300 h-11"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-xs font-accent uppercase tracking-wider text-gray-500">
                          Doğum Tarihi *
                        </Label>
                        <Input
                          name="birth_date"
                          type="date"
                          value={form.birth_date}
                          onChange={handleChange}
                          data-testid="birth-date-picker"
                          className="mt-1.5 rounded-none border-zinc-300 h-11"
                          min="2010-01-01"
                          max="2021-12-31"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-accent uppercase tracking-wider text-gray-500">
                          Cinsiyet *
                        </Label>
                        <div className="grid grid-cols-2 gap-3 mt-1.5">
                          {["Erkek", "Kız"].map((g) => (
                            <button
                              key={g}
                              type="button"
                              data-testid={`gender-option-${g.toLowerCase()}`}
                              onClick={() => handleSelectChange("gender", g)}
                              className={`p-3 border-2 text-center transition-all duration-200 ${
                                form.gender === g
                                  ? "border-burgundy bg-burgundy/5 text-burgundy"
                                  : "border-zinc-200 hover:border-zinc-300 text-zinc-600"
                              }`}
                            >
                              <span className="font-heading text-sm font-bold uppercase tracking-wider">{g}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-accent uppercase tracking-wider text-gray-500">
                        Branş Seçimi *
                      </Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                        {["Voleybol", "Basketbol", "Cimnastik", "Yaz Okulu"].map((branch) => (
                          <button
                            key={branch}
                            type="button"
                            data-testid={`branch-option-${branch.toLowerCase()}`}
                            onClick={() => handleSelectChange("branch", branch)}
                            className={`p-4 border-2 text-center transition-all duration-200 ${
                              form.branch === branch
                                ? "border-burgundy bg-burgundy/5 text-burgundy"
                                : "border-zinc-200 hover:border-zinc-300 text-zinc-600"
                            }`}
                          >
                            <span className="font-heading text-sm font-bold uppercase tracking-wider">{branch}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2 - Veli */}
                {currentStep === 2 && (
                  <div data-testid="step-2" className="space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-zinc-200 mb-6">
                      <div className="w-10 h-10 bg-burgundy/5 border border-burgundy/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-burgundy" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-[#1A1A1A]">
                          Veli Bilgileri
                        </h2>
                        <p className="text-xs text-zinc-500">Velinin iletişim bilgilerini girin</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="parent_name" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                          Veli Adı *
                        </Label>
                        <Input
                          id="parent_name"
                          name="parent_name"
                          value={form.parent_name}
                          onChange={handleChange}
                          placeholder="Veli adını girin"
                          data-testid="parent-name"
                          className="mt-1.5 rounded-none border-zinc-300 h-11"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="parent_surname" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                          Veli Soyadı *
                        </Label>
                        <Input
                          id="parent_surname"
                          name="parent_surname"
                          value={form.parent_surname}
                          onChange={handleChange}
                          placeholder="Veli soyadını girin"
                          data-testid="parent-surname"
                          className="mt-1.5 rounded-none border-zinc-300 h-11"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="parent_phone" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                          Veli Telefon *
                        </Label>
                        <Input
                          id="parent_phone"
                          name="parent_phone"
                          value={form.parent_phone}
                          onChange={handleChange}
                          placeholder="05XX XXX XX XX"
                          data-testid="parent-phone"
                          className="mt-1.5 rounded-none border-zinc-300 h-11"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="parent_email" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                          Veli E-posta *
                        </Label>
                        <Input
                          id="parent_email"
                          name="parent_email"
                          type="email"
                          value={form.parent_email}
                          onChange={handleChange}
                          placeholder="ornek@email.com"
                          data-testid="parent-email"
                          className="mt-1.5 rounded-none border-zinc-300 h-11"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="address" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                        Adres
                      </Label>
                      <Textarea
                        id="address"
                        name="address"
                        rows={3}
                        value={form.address}
                        onChange={handleChange}
                        placeholder="Ev adresinizi girin (isteğe bağlı)"
                        data-testid="address"
                        className="mt-1.5 rounded-none border-zinc-300"
                      />
                    </div>
                  </div>
                )}

                {/* Step 3 - Ek Bilgiler */}
                {currentStep === 3 && (
                  <div data-testid="step-3" className="space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-zinc-200 mb-6">
                      <div className="w-10 h-10 bg-burgundy/5 border border-burgundy/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-burgundy" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-[#1A1A1A]">
                          Ek Bilgiler
                        </h2>
                        <p className="text-xs text-zinc-500">Sağlık ve diğer ek bilgiler (isteğe bağlı)</p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="health_notes" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                        Sağlık Bilgileri / Alerjiler
                      </Label>
                      <Textarea
                        id="health_notes"
                        name="health_notes"
                        rows={3}
                        value={form.health_notes}
                        onChange={handleChange}
                        data-testid="health-notes"
                        className="mt-1.5 rounded-none border-zinc-300"
                        placeholder="Varsa sağlık durumu veya alerjiler hakkında bilgi veriniz"
                      />
                    </div>

                    <div>
                      <Label className="text-xs font-accent uppercase tracking-wider text-gray-500">
                        Bizi Nereden Duydunuz?
                      </Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                        {["Sosyal Medya", "Arkadaş Tavsiyesi", "İnternet", "Üniversite", "Diğer"].map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`hear-option-${opt.toLowerCase().replace(/\s/g, "-")}`}
                            onClick={() => handleSelectChange("how_did_you_hear", opt)}
                            className={`p-2.5 border text-center transition-all duration-200 text-xs ${
                              form.how_did_you_hear === opt
                                ? "border-burgundy bg-burgundy/5 text-burgundy font-semibold"
                                : "border-zinc-200 hover:border-zinc-300 text-zinc-500"
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="notes" className="text-xs font-accent uppercase tracking-wider text-gray-500">
                        Ek Notlar
                      </Label>
                      <Textarea
                        id="notes"
                        name="notes"
                        rows={3}
                        value={form.notes}
                        onChange={handleChange}
                        data-testid="notes"
                        className="mt-1.5 rounded-none border-zinc-300"
                        placeholder="Eklemek istediğiniz herhangi bir not"
                      />
                    </div>

                    {/* Özet */}
                    <div className="mt-8 p-5 bg-surface border border-zinc-200">
                      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A1A] mb-3">
                        Kayıt Özeti
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-zinc-400 text-xs">Öğrenci:</span>
                          <p className="font-medium text-[#1A1A1A]">{form.student_name} {form.student_surname}</p>
                        </div>
                        <div>
                          <span className="text-zinc-400 text-xs">Branş:</span>
                          <p className="font-medium text-burgundy">{form.branch}</p>
                        </div>
                        <div>
                          <span className="text-zinc-400 text-xs">Veli:</span>
                          <p className="font-medium text-[#1A1A1A]">{form.parent_name} {form.parent_surname}</p>
                        </div>
                        <div>
                          <span className="text-zinc-400 text-xs">Telefon:</span>
                          <p className="font-medium text-[#1A1A1A]">{form.parent_phone}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="px-6 sm:px-8 lg:px-10 pb-6 sm:pb-8 lg:pb-10 flex items-center justify-between gap-4">
                {currentStep > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    data-testid="prev-step-btn"
                    className="rounded-none border-zinc-300 font-heading uppercase tracking-wider text-sm px-6 py-5"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Geri
                  </Button>
                ) : (
                  <div />
                )}

                {currentStep < 3 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    data-testid="next-step-btn"
                    className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-5"
                  >
                    Devam Et
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="pre-registration-submit"
                    className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-5"
                  >
                    {loading ? "Gönderiliyor..." : "Başvuruyu Tamamla"}
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>

            {/* Güven Bilgisi */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                <span className="text-xs text-zinc-500">Bilgileriniz güvende</span>
              </div>
              <div className="hidden sm:block w-1 h-1 bg-zinc-300 rounded-full" />
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                <span className="text-xs text-zinc-500">Sorularınız için: 0543 647 01 83</span>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
